// LuviaRx product catalog helpers
// =============================================================================
// The catalog itself lives in MongoDB and is fetched via /api/products by
// CatalogContext.jsx. This file only holds pure helpers + static brand
// constants (categories, promo config) that don't change at runtime.
// =============================================================================

// Helper to resolve a variant's image, falling back to the parent product's image.
export const resolveImage = (product, variant) => ({
  src: (variant && variant.image) || product?.image,
  fallback: (variant && variant.imageFallback) || product?.imageFallback,
});

// -----------------------------------------------------------------------------
// Promotions (kept client-side as configuration; mirrored in backend for math).
// -----------------------------------------------------------------------------
export const PROMOS = {
  freeBacWater: {
    enabled: true,
    label: "Included",
    text: "every compound vial ships with a complimentary 3 mL Bac Water vial.",
    excludeSlugs: ["bac-water"],
  },
  starterKit: {
    enabled: true,
    threshold: 500,
    publicText: "Free Research Starter Kit on every order over $500",
    qualifiedText: "Free Research Starter Kit unlocked. Details included with your order confirmation.",
    progressText: (remaining) =>
      `Add $${remaining.toFixed(2)} more to unlock a free Research Starter Kit.`,
  },
};

// -----------------------------------------------------------------------------
// Categories — these labels are the ONLY headers used site-wide.
// -----------------------------------------------------------------------------
export const CATEGORIES = [
  { id: "endocrine",          label: "Endocrine Research" },
  { id: "metabolic",          label: "Metabolic Pathway Research" },
  { id: "growth-factors",     label: "Peptide Growth Factors" },
  { id: "cellular-signaling", label: "Cellular Signaling Research" },
  { id: "neuroprotective",    label: "Neuroprotective Research" },
  { id: "mitochondrial",      label: "Mitochondrial Research" },
  { id: "regenerative",       label: "Regenerative Research" },
  { id: "research-supplies",  label: "Research Supplies & Reagents" },
];

// -----------------------------------------------------------------------------
// Helpers — all stateless. Pass in the product list (from useCatalog) where
// needed. Per-product helpers operate on a single product object.
// -----------------------------------------------------------------------------
export const getProduct = (products, slug) =>
  (products || []).find((p) => p.slug === slug) || null;

export const getVariant = (product, variantId) => {
  if (!product) return null;
  return (
    (product.variants || []).find((v) => v.id === variantId) ||
    (product.variants || []).find((v) => v.id === product.defaultVariant) ||
    (product.variants || [])[0] ||
    null
  );
};

export const hasMultipleVariants = (product) => (product?.variants?.length || 0) > 1;

export const minPrice = (product) =>
  Math.min(...(product?.variants || []).map((v) => v.price));

export const productInStock = (product) =>
  (product?.variants || []).some((v) => v.inStock && v.stock > 0);

export const stockLabel = (variant) => {
  if (!variant || !variant.inStock || variant.stock <= 0) {
    return { label: "Out of stock", tone: "out" };
  }
  if (variant.stock <= 5) return { label: `Only ${variant.stock} left`, tone: "low" };
  return { label: "In stock", tone: "ok" };
};

export const productsByCategory = (products, categoryId) =>
  (products || []).filter((p) => p.category === categoryId);

export const showsFreeBacWater = (product) =>
  PROMOS.freeBacWater.enabled &&
  !PROMOS.freeBacWater.excludeSlugs.includes(product?.slug);
